import { db } from '../../config/firebase/config';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  serverTimestamp,
  DocumentReference,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  deleteDoc,
  setDoc
} from 'firebase/firestore';
import { 
  Restaurant, 
  RestaurantSettings, 
  UserProfile,
  DEFAULT_RESTAURANT_SETTINGS,
  UserRole
} from '../../types/restaurant';
import { logAction } from '../logging/logService';

export interface StaffProfile {
  id: string;
  name: string;
  active: boolean;
  restaurantId: string;
}

export async function getStaffProfiles(restaurantId: string): Promise<StaffProfile[]> {
  const q = query(collection(db, 'staffProfiles'), where('restaurantId', '==', restaurantId));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...(doc.data() as Omit<StaffProfile, 'id'>)
  }));
}

export async function getUserProfile(userId: string): Promise<UserProfile> {
  const userDocRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userDocRef);
  
  if (!userDoc.exists()) {
    throw new Error('No se encontró el perfil del usuario');
  }
  
  const data = userDoc.data();
  return {
    id: userDoc.id,
    email: data.email,
    displayName: data.displayName,
    role: data.role,
    restaurantId: data.restaurantId,
    active: data.active,
    createdAt: data.createdAt?.toDate() ?? new Date(),
    lastLogin: data.lastLogin?.toDate() ?? null
  };
}

export async function getRestaurant(restaurantId: string): Promise<Restaurant> {
  const restaurantDocRef = doc(db, 'restaurants', restaurantId);
  const restaurantDoc = await getDoc(restaurantDocRef);
  
  if (!restaurantDoc.exists()) {
    throw new Error('No se encontró información del restaurante');
  }
  
  const data = restaurantDoc.data();
  return {
    id: restaurantDoc.id,
    name: data.name,
    logoUrl: data.logoUrl,
    settings: data.settings ?? DEFAULT_RESTAURANT_SETTINGS,
    active: data.active ?? true,
    createdAt: data.createdAt?.toDate() ?? new Date(),
    updatedAt: data.updatedAt?.toDate() ?? new Date(),
    // POS Integration (Super Admin Only)
    integrationMode: data.integrationMode ?? 'none',  // Default to 'none' for existing restaurants
    posConfig: data.posConfig,
    processor: data.processor,
    stripeConfig: data.stripeConfig,
    cardnetConfig: data.cardnetConfig,
    azulConfig: data.azulConfig,
  };
}

export async function updateRestaurantSettings(
  restaurantId: string, 
  settings: Partial<RestaurantSettings>,
  userId: string,
  userEmail: string,
  userRole: UserRole
): Promise<void> {
  const restaurantRef: DocumentReference = doc(db, 'restaurants', restaurantId);
  
  await updateDoc(restaurantRef, {
    'settings': settings,
    'updatedAt': serverTimestamp()
  });

  await logAction({
    type: 'settings_update',
    userId,
    userEmail,
    restaurantId,
    origin: 'admin',
    details: { settings }
  });
}


export async function updateUserLastLogin(userId: string): Promise<void> {
  const userRef: DocumentReference = doc(db, 'users', userId);
  
  await updateDoc(userRef, {
    'lastLogin': serverTimestamp()
  });
}

export async function checkUserAccess(userId: string, restaurantId: string): Promise<boolean> {
  const userProfile = await getUserProfile(userId);
  return userProfile.restaurantId === restaurantId && userProfile.active;
}

export async function addStaffMember(
  restaurantId: string,
  email: string,
  role: UserRole,
  adminUserId: string,
  adminEmail: string,
  adminRole: UserRole
): Promise<void> {
  console.log('Starting addStaffMember with:', { email, role, restaurantId });
  
  // Check for duplicate email
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('email', '==', email));
  const snapshot = await getDocs(q);
  
  if (!snapshot.empty) {
    console.log('Duplicate email found:', email);
    await logAction({
      type: 'error',
      userId: adminUserId,
      userEmail: adminEmail,
      restaurantId,
      origin: 'admin',
      details: { error: 'Email duplicado', email }
    });
    throw new Error('Este correo ya está registrado');
  }
  
  console.log('Data being sent to Firestore:', {
    email,
    role,
    restaurantId,
    active: true,
    displayName: '',
    createdAt: 'serverTimestamp()',
    lastLogin: null
  });
  const docRef = await addDoc(usersRef, {
    email,
    role,
    restaurantId,
    active: true,
    displayName: '',
    createdAt: serverTimestamp(),
    lastLogin: null
  });
  console.log('New user document created with ID:', docRef.id);

  await logAction({
    type: 'staff_add',
    userId: adminUserId,
    userEmail: adminEmail,
    restaurantId,
    origin: 'admin',
    details: { email, role }
  });
  console.log('Staff member added successfully');
}

export async function setRestaurantLogo(
  restaurantId: string,
  logoUrl: string,
  userId: string,
  userEmail: string,
  userRole: UserRole
): Promise<void> {
  const restaurantRef: DocumentReference = doc(db, 'restaurants', restaurantId);
  
  await updateDoc(restaurantRef, {
    'logoUrl': logoUrl,
    'updatedAt': serverTimestamp()
  });

  await logAction({
    type: 'logo_update',
    userId,
    userEmail,
    restaurantId,
    origin: 'admin',
    details: { logoUrl }
  });
}

export async function toggleRestaurantActive(
  restaurantId: string,
  active: boolean,
  userId: string,
  userEmail: string,
  userRole: UserRole
): Promise<void> {
  const restaurantRef: DocumentReference = doc(db, 'restaurants', restaurantId);
  
  await updateDoc(restaurantRef, {
    'active': active,
    'updatedAt': serverTimestamp()
  });

  await logAction({
    type: 'restaurant_activate_toggle',
    userId,
    userEmail,
    restaurantId,
    origin: 'admin',
    details: { active }
  });
}

export async function updateRestaurantName(
  restaurantId: string,
  name: string,
  userId: string,
  userEmail: string,
  userRole: UserRole
): Promise<void> {
  const restaurantRef: DocumentReference = doc(db, 'restaurants', restaurantId);

  await updateDoc(restaurantRef, {
    'name': name,
    'updatedAt': serverTimestamp()
  });

  await logAction({
    type: 'settings_update',
    userId,
    userEmail,
    restaurantId,
    origin: 'admin',
    details: { name_updated_to: name }
  });
}

export async function removeStaffMember(
  restaurantId: string,
  userId: string,
  adminUserId: string,
  adminEmail: string,
  adminRole: UserRole
): Promise<void> {
  const userRef: DocumentReference = doc(db, 'users', userId);
  
  await updateDoc(userRef, {
    'active': false,
    'updatedAt': serverTimestamp()
  });

  await logAction({
    type: 'staff_remove',
    userId: adminUserId,
    userEmail: adminEmail,
    restaurantId,
    origin: 'admin',
    details: { removedUserId: userId }
  });
}

export async function updateStaffRole(
  restaurantId: string,
  userId: string,
  newRole: UserRole,
  adminUserId: string,
  adminEmail: string,
  adminRole: UserRole
): Promise<void> {
  const userRef: DocumentReference = doc(db, 'users', userId);
  
  await updateDoc(userRef, {
    'role': newRole,
    'updatedAt': serverTimestamp()
  });

  await logAction({
    type: 'staff_role_change',
    userId: adminUserId,
    userEmail: adminEmail,
    restaurantId,
    origin: 'admin',
    details: { 
      userId, 
      newRole 
    }
  });
}

export interface MenuItem {
  id?: string;
  name: string;
  price: number;
  category: string;
  active: boolean;
  restaurantId: string;
}

// Helper function for common log fields
function baseLogInfo(userId: string, userEmail: string, restaurantId: string) {
  return { userId, userEmail, restaurantId, origin: 'admin' as const };
}

/**
 * Add a new menu item to Firestore.
 */
export async function addMenuItem(
  data: Omit<MenuItem, 'id'>,
  userId: string,
  userEmail: string,
  userRole: UserRole
): Promise<string> {
  const docRef = await addDoc(collection(db, 'menuItems'), {
    ...data,
    createdAt: serverTimestamp()
  });

  await logAction({
    type: 'menu_item_add',
    ...baseLogInfo(userId, userEmail, data.restaurantId),
    details: { menuItem: data }
  });

  return docRef.id;
}

/**
 * Update a menu item in Firestore.
 */
export async function updateMenuItem(
  itemId: string,
  data: Partial<Omit<MenuItem, 'id' | 'restaurantId'>>,
  userId: string,
  userEmail: string,
  restaurantId: string,
  userRole: UserRole
): Promise<void> {
  const itemRef = doc(db, 'menuItems', itemId);
  
  await setDoc(itemRef, {
    ...data,
    updatedAt: serverTimestamp()
  }, { merge: true });

  await logAction({
    type: 'menu_item_update',
    ...baseLogInfo(userId, userEmail, restaurantId),
    details: { itemId, updates: data }
  });
}

/**
 * Delete a menu item from Firestore.
 */
export async function deleteMenuItem(
  itemId: string,
  userId: string,
  userEmail: string,
  restaurantId: string,
  userRole: UserRole
): Promise<void> {
  const itemRef = doc(db, 'menuItems', itemId);
  
  await deleteDoc(itemRef);

  await logAction({
    type: 'menu_item_delete',
    ...baseLogInfo(userId, userEmail, restaurantId),
    details: { itemId }
  });
}

/**
 * Get restaurant by table ID
 * Used when customer scans QR code
 */
export async function getRestaurantByTableId(tableId: string): Promise<Restaurant | null> {
  try {
    // First get the table to find restaurant ID
    const tableRef = doc(db, 'mesas', tableId);
    const tableSnap = await getDoc(tableRef);
    
    if (!tableSnap.exists()) {
      return null;
    }
    
    const tableData = tableSnap.data();
    return await getRestaurant(tableData.restaurantId);
  } catch (error) {
    console.error('Error fetching restaurant by table:', error);
    throw error;
  }
}

/**
 * Check if restaurant has payment processor configured
 */
export function hasPaymentProcessor(restaurant: Restaurant): boolean {
  if (!restaurant.processor || restaurant.processor === 'none') {
    return false;
  }
  
  if (restaurant.processor === 'azul') {
    return !!(
      restaurant.azulConfig?.merchantId && 
      (restaurant.azulConfig?.secretName || 
       (restaurant.azulConfig?.auth1 && restaurant.azulConfig?.auth2))
    );
  }
  
  if (restaurant.processor === 'cardnet') {
    return !!(restaurant.cardnetConfig?.merchantId);
  }
  
  return false;
}

/**
 * Get display name for processor
 */
export function getProcessorName(processor: string): string {
  const names: Record<string, string> = {
    'azul': 'Azul',
    'cardnet': 'CardNet',
    'none': 'No configurado'
  };
  return names[processor] || processor;
}